import React, { useState, useEffect } from "react";
import {
  ICreateExpenseData,
  ICreateSalesData,
  ICustomer,
  IVendor,
} from "../../../@types";
import { CloseIcon, CloseIconSmall, SearchIcon } from "../../../assets/icons";
import { useCustomerSearch, useVendorSearch } from "../../../hooks";
import { useCustomerStore } from "../../../state";
import { SearchInput } from "../../common";
import shallow from "zustand/shallow";
import CreateVendorModal from "./createVendor";
import { useVendorStore } from "../../../state/useVendorStore";
import SelectedMarkIcon from "../../../assets/icons/SelectedIcon";

interface CustomerInfoProps {
  handleSelectedVendor: (selectedVendor: IVendor | null) => void;
  formData: ICreateExpenseData;
  vendor: IVendor | undefined;
  onClose: () => void;
  vendorName: IVendor | undefined;
}

const SelectVendor: React.FC<CustomerInfoProps> = ({
  handleSelectedVendor,
  formData,
  vendor,
  onClose,
  vendorName,
}) => {
  const {
    data: vendors,
    isSearching: isVendorSearching,
    handleSearch: handleVendorSearch,
    setSearchedDone,
    searchedDone,
  } = useVendorSearch();

  // Fetch vendors from Zustand Store
  const vendorNew = useVendorStore((state) => state.vendors);
  const { fetchVendors } = useVendorStore();
  const [showCreateVendor, setShowCreateVendor] = useState(false);
  const [displayedVendors, setDisplayedVendors] = useState<IVendor[]>([]);
  const [isLoadingVendors, setIsLoadingVendors] = useState(true);
  const [selectedVendor, setSelectedVendor] = useState<IVendor | null>(null);

  useEffect(() => {
    const fetchVendorData = async () => {
      setIsLoadingVendors(true);
      await fetchVendors();
      setIsLoadingVendors(false);
    };
    fetchVendorData();
  }, [fetchVendors]);

  const vendorsToDisplay = (vendors.length > 0 ? vendors : vendorNew).slice(0, 3);

  const handleAddNewVendor = () => {
    setShowCreateVendor(true);
  };

  const renderVendorItem = (vendor: IVendor) => (
    <div
      key={vendor.id}
      className={`mt-2 flex justify-between items-center p-2 rounded-md cursor-pointer ${
        selectedVendor?.id === vendor.id
          ? "bg-blue-100 border-2 border-blue-500"
          : "hover:bg-gray-100"
      }`}
      onClick={() => {
        handleSelectedVendor(vendor);
        onClose();
        setSelectedVendor(vendor);
      }}
    >
      <div>
        <h5 className="font-medium text-[14px]">{vendor.name}</h5>
      </div>
      {selectedVendor?.id === vendor.id ? (
        <SelectedMarkIcon />
      ) : (
        <div className="rounded-full w-4 h-4 border border-gray-600" />
      )}
    </div>
  );

  return (
    <>
      {/* Desktop */}
      <div className=" hidden lg:block md:block w-[500px] h-[400px] relative">
        <button
          className="absolute top-2 right-2 py-[0.2em]  pr-4"
          aria-label="Close modal"
          onClick={onClose}
        >
          <CloseIcon color="black" />
        </button>
        <h1 className="font-normal lg:text-[24px] text-[18px]">
          Select a vendor
        </h1>
        <div className=" mt-6 md:mt-[1em] flex flex-col items-center justify-center w-full">
          <SearchInput
            placeholder="Search for Vendor"
            data={vendors}
            displayKey="name"
            isLoading={isVendorSearching}
            setSearchedDone={setSearchedDone}
            searchedDone={searchedDone}
            value={formData.vendorName}
            selected={vendorName}
            onSelect={(selected) => {
              handleSelectedVendor(selected as IVendor);
              setSelectedVendor(selected as IVendor);
            }}
            handleChangeText={handleVendorSearch}
            emptyMessage={"This vendor is not found"}
            clearIcon={CloseIcon}
            clearIconClass="xl:mr-[1em] mr-[0.5em] md:mr-[3.2em] biglg:mr-[2em] lg:mr-[1em]"
            classNames="w-full border border-gray-300 bg-white max-h-[48px] min-h-[48px] placeholder:text-[15.25px] rounded  focus:outline-none focus:shadow-outline appearance-none focus:ring-0 leading-normal"
            dropdownClassName="w-full "
            dropdownStyle={{
              maxHeight: "800px",
              overflowY: "auto",
            }}
            searchKey={"name"}
            icon={<SearchIcon />}
          />

          {isLoadingVendors ? (
            <div className="mt-2 space-y-4 mx-auto flex w-full">
              <div className="inset-y-0 right-6 flex items-center mx-auto w-full justify-center">
                <div className="w-7 h-7 border-4 border-gray-200 border-t-[#044E97] rounded-full animate-spin"></div>
              </div>
            </div>
          ) : vendorsToDisplay.length > 0 ? (
            <div className="mt-2 space-y-4 w-full">
              {vendorsToDisplay.map(renderVendorItem)}
            </div>
          ) : (
            <div>
              {!isLoadingVendors && vendorsToDisplay.length === 0 && (
                <p className="text-center">No vendors found</p>
              )}
            </div>
          )}

          <div className="flex  absolute bottom-3 gap-5 w-full">
            <button
              className="text-[#044E97] border border-[#044E97] w-full text-center p-3 rounded-md font-semibold lg:mt-0 xl:mt-0 biglg:mt-0 mt-[1em] md:mt-[-0.3em]"
              onClick={handleAddNewVendor}
            >
              Add New Vendor
            </button>
          </div>
        </div>
        {showCreateVendor && (
          <CreateVendorModal
            showSideModal={showCreateVendor}
            setShowSideModal={setShowCreateVendor}
            handleSelectedVendor={handleSelectedVendor}
            onVendorCreate={(vendor) => {
              handleSelectedVendor(vendor);
              setSelectedVendor(vendor);
              setShowCreateVendor(false);
            }}
            onClose={() => setShowCreateVendor(false)}
          />
        )}
      </div>

      {/* Mobile */}
      <div className="relative">
        <div className="lg:hidden md:hidden fixed bg-white w-full h-full bottom-0 right-0 left-0 ">
          <button
            className="absolute top-2 right-2 mt-[2em] py-[0.2em]  pr-4"
            aria-label="Close modal"
            onClick={onClose}
          >
            <CloseIcon />
          </button>
          <h1 className="lg:text-[25px] font-semibold text-[18px] mt-[2em] pl-[1.5em]">
            Select A Vendor
          </h1>
          <div className=" mt-6 flex flex-col mx-auto text-center px-6">
            <SearchInput
              placeholder="Search for Vendor"
              data={vendors}
              displayKey="name"
              isLoading={isVendorSearching}
              value={formData.vendorName}
              selected={vendorName}
              onSelect={(selected) => {
                handleSelectedVendor(selected as IVendor);
                setSelectedVendor(selected as IVendor);
              }}
              handleChangeText={handleVendorSearch}
              emptyMessage={"This vendor is not found"}
              clearIcon={CloseIcon}
              clearIconClass="xl:mr-[1em] md:mr-[3.2em] biglg:mr-[2em] lg:mr-[1em]"
              classNames="w-full border border-gray-300 bg-white max-h-[48px] min-h-[48px] placeholder:text-[15.25px] rounded  focus:outline-none focus:shadow-outline appearance-none focus:ring-0 leading-normal"
              dropdownClassName="w-full"
              dropdownStyle={{
                maxHeight: "800px",
                overflowY: "auto",
              }}
              searchKey={"name"}
              icon={<SearchIcon />}
            />
          </div>

          {isVendorSearching ? (
            <div className="mt-5 flex justify-center">
              <div className="w-7 h-7 border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
            </div>
          ) : (
            <div className="mt-5 space-y-4 w-full px-6">
              {vendorsToDisplay.length > 0 ? (
                vendorsToDisplay.map(renderVendorItem)
              ) : (
                <p className="text-center">No vendors found</p>
              )}
            </div>
          )}

          <div className="flex absolute items-center justify-center bottom-10 320:left-[0.5em] 375:left-[2em] 412:left-[4em] gap-4">
            <button
              className=" border border-[#A4A4A4] w-[150px]  flex justify-center items-center text-center p-3 rounded-md lg:mt-0 xl:mt-0 biglg:mt-0 mt-[1em] text-[16px]"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              className="text-[white] border bg-[#044E97] w-[150px]  flex justify-center items-center text-center p-3 rounded-md font-semibold lg:mt-0 xl:mt-0 biglg:mt-0 mt-[1em] text-[16px]"
              onClick={handleAddNewVendor}
            >
              Add New Vendor
            </button>
          </div>

          <CreateVendorModal
            showSideModal={showCreateVendor}
            setShowSideModal={setShowCreateVendor}
            handleSelectedVendor={handleSelectedVendor}
          />
        </div>
      </div>
    </>
  );
};

export default SelectVendor;